import { useState } from 'react';
import { useTheme } from '../context/ThemeContext';

const reasons = [
  { title: 'Architect-Led Engineering', desc: 'Every system is designed and deployed by senior developers. We don\'t use your critical infrastructure as a training ground for juniors.' },
  { title: 'Radical Transparency', desc: 'Direct access to the developers writing your code. We bypass account managers to give you unfiltered, weekly technical sprint reviews.' },
  { title: 'Predictable Execution', desc: 'Through rigorous scoping and agile sprint cycles, we eliminate scope creep and deliver complex integrations exactly when promised.' },
  { title: 'Scalable Infrastructure', desc: 'Launch is just day one. We build scalable codebases designed for high-traffic environments, backed by proactive performance monitoring.' },
];

function WhyItem({ reason, index, colors }) {
  const [hovered, setHovered] = useState(false);
  
  return (
    <li
      style={{
        display: 'flex', 
        alignItems: 'flex-start', 
        gap: '1.5rem',
        padding: '1.5rem', 
        border: '1px solid', 
        borderRadius: '8px',
        transition: 'all 0.3s ease', 
        cursor: 'default',
        borderColor: hovered ? colors.text : 'transparent', 
        background: hovered ? 'rgba(255, 255, 255, 0.02)' : 'transparent',
        transform: hovered ? 'translateX(8px)' : 'translateX(0)',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span style={{ 
        color: hovered ? colors.text : colors.muted, 
        fontFamily: "'Inter', sans-serif",
        fontSize: '1.1rem', 
        flexShrink: 0, 
        fontWeight: 800,
        opacity: hovered ? 1 : 0.5,
        transition: 'all 0.3s ease',
        marginTop: '2px'
      }}>
        {String(index + 1).padStart(2, '0')}
      </span>
      
      <div>
        <h4 style={{ 
          fontSize: '1.05rem', 
          fontWeight: 600, 
          marginBottom: '0.4rem', 
          color: colors.text,
          letterSpacing: '0.5px'
        }}>
          {reason.title}
        </h4>
        <p style={{ 
          fontSize: '0.85rem', 
          color: colors.muted, 
          lineHeight: 1.7, 
          fontWeight: 300,
          margin: 0
        }}>
          {reason.desc}
        </p>
      </div>
    </li>
  );
}

function MetricCard({ value, label, spanCols = 1, colors }) {
  return (
    <div className="metric-card" style={{
      gridColumn: `span ${spanCols}`,
      background: colors.surface,
      border: `1px solid ${colors.border}`,
      borderRadius: '12px',
      padding: '2rem 1.5rem',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
      transition: 'transform 0.3s ease, border-color 0.3s ease',
    }}>
      {/* Subtle corner glow effect */}
      <div style={{
        position: 'absolute',
        top: '-20px',
        right: '-20px',
        width: '100px',
        height: '100px',
        background: colors.text,
        opacity: 0.04,
        filter: 'blur(30px)',
        borderRadius: '50%',
        pointerEvents: 'none'
      }} />
      
      <h3 style={{
        fontSize: 'clamp(2rem, 3.5vw, 3.2rem)',
        fontWeight: 800,
        color: colors.text,
        marginBottom: '0.5rem',
        fontFamily: "'Inter', sans-serif",
        letterSpacing: '-1px',
        lineHeight: 1,
        margin: '0 0 0.5rem 0'
      }}>
        {value}
      </h3>
      <p style={{
        color: colors.muted,
        fontSize: '0.8rem',
        fontWeight: 600,
        letterSpacing: '1px',
        textTransform: 'uppercase',
        margin: 0
      }}>
        {label}
      </p>
    </div>
  );
}

export default function WhyUs() {
  const { colors } = useTheme();
  
  const s = {
    section: {
      padding: '100px 5vw', 
      background: colors.bg, // Assuming you want it consistent with other sections
    },
    title: {
      fontFamily: "'Inter', sans-serif", 
      fontWeight: 700,
      fontSize: 'clamp(2rem, 3.6vw, 3.2rem)',
      lineHeight: 1.05, 
      letterSpacing: '2px', 
      color: colors.heading, 
      textAlign: 'center',
      margin: '0 0 1rem',
    },
    desc: { 
      color: colors.muted, 
      fontSize: '1rem', 
      lineHeight: 1.8, 
      fontWeight: 300,
      maxWidth: '700px',
      textAlign: 'center',
      margin: '0 auto 4rem',
    },
    contentGrid: {
      display: 'grid',
      gridTemplateColumns: '1.1fr 0.9fr', // Gives slightly more room to text on desktop
      gap: '5rem',
      alignItems: 'center',
    },
    left: {},
    list: { 
      listStyle: 'none', 
      display: 'flex', 
      flexDirection: 'column', 
      gap: '0.5rem',
      padding: 0,
      margin: 0
    },
    visual: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2rem',
    },
    bentoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '1.5rem',
    }
  };

  return (
    <section id="why-us" style={s.section} className="why-section-inner">
      <h2 style={s.title}>TECHNOLOGY WITH PURPOSE</h2>
      <p style={s.desc}>
        Every system we build serves a business goal, and is judged by its results.
      </p>
      <div style={s.contentGrid} className="why-content-grid">
      <div style={s.left}>
        <ul style={s.list}>
          {reasons.map((r, i) => (
            <WhyItem key={r.title} index={i} reason={r} colors={colors} />
          ))}
        </ul>
      </div>

      <div style={s.visual} className="why-visual">
        
        {/* Subtle Metric Label */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', paddingLeft: '0.5rem' }}>
          <div style={{ width: '6px', height: '6px', background: colors.text, borderRadius: '50%' }} />
          <span style={{ 
            fontSize: '0.75rem', 
            fontWeight: 600, 
            letterSpacing: '2px', 
            textTransform: 'uppercase', 
            color: colors.muted 
          }}>
             Deck For Tech Performance Standards
          </span>
        </div>

        {/* Bento Grid */}
        <div style={s.bentoGrid} className="bento-container">
          <MetricCard 
            value="99.9%" 
            label="Target SLA Uptime" 
            colors={colors} 
          />
          <MetricCard 
            value="<200ms" 
            label="API Response Time" 
            colors={colors} 
          />
          <MetricCard 
            value="20+" 
            label="Retail CRM & Commerce Integrations" 
            spanCols={2} 
            colors={colors} 
          />
        </div>
        
      </div>
      </div>

      <style>{`
        .metric-card:hover {
          border-color: ${colors.text} !important;
          transform: translateY(-4px);
        }

        @media (max-width: 1000px) {
          .why-content-grid { 
            grid-template-columns: 1fr !important; 
            gap: 4rem !important; 
          }
          
          /* Ensures the visual loads beautifully on mobile rather than disappearing */
          .why-visual {
            order: -1; /* Optionally moves the metrics above the checklist on mobile */
          }
        }
        
        @media (max-width: 600px) {
          .why-section-inner h2 { 
            white-space: normal !important; 
          }
          
          .bento-container {
            grid-template-columns: 1fr !important; /* Stacks the bento cards vertically on small phones */
          }
          
          .metric-card {
            grid-column: span 1 !important;
          }
        }
      `}</style>
    </section>
  );
}